import { illustrationObjectSchema } from '@platform/schema/content';
import {
  illustrationCopy,
  illustrationGroupCopy,
  illustrationGroupOf,
} from '@platform/schema/illustration';

import type { IllustrationObject } from '@platform/schema/content';

/**
 * What the console says about a lesson's card illustration — GEN-42.
 *
 * "Re-pick object" beside `Illustration: balance` misled twice: "object" is the column's
 * word, and the button promised a picture when `013` §13 was amended to mean re-reading
 * `objectFor` over the current scenes.
 *
 * The defect is the unchanged case. Scenes that still point the same way write `balance`
 * over `balance`, so a working control looked like a broken one.
 */

export interface IllustrationNote {
  severity: 'info' | 'success' | 'error';
  message: string;
}

const name = (object: IllustrationObject): string =>
  illustrationCopy[object].label;

/**
 * What one shape is called and what it says, in the one wording the console uses.
 *
 * `illustrationLine` and the create form's option list both need it, and describing the
 * six shapes twice is how they start to disagree about what `bridge` means.
 */
export const describeObject = (object: IllustrationObject): string => {
  const { label, meaning } = illustrationCopy[object];

  return `${label} — ${meaning}`;
};

/**
 * The heading an object sits under in a picker — VIS-13.
 *
 * Thirty-nine options, so the create form and the settings field both group, and both
 * through this: a second spelling of `Momentum` is how two screens start disagreeing
 * about what a group is called.
 */
export const groupOfObject = (object: IllustrationObject): string =>
  illustrationGroupCopy[illustrationGroupOf(object)];

/**
 * The object the API returned, or nothing.
 *
 * The generated SDK types this column as `enum | unknown`: a nullable enum leaves the
 * generator with no name for the null arm, and the union swallows the enum. Parsing is the
 * only narrowing available that is not an assertion — `source.ts` widens for the same
 * reason at the same seam.
 */
export function illustrationFrom(
  value: unknown,
): IllustrationObject | undefined {
  const parsed = illustrationObjectSchema.safeParse(value);

  return parsed.success ? parsed.data : undefined;
}

/**
 * The line above the control: what this lesson is drawn with, and why that shape.
 *
 * Absent is `null` or `undefined` — the column is nullable and the SDK makes the field
 * optional, so demanding one shape here would put a `?? null` at every call site. That is
 * the argument `publish-rules.ts` makes about its own key columns.
 */
export function illustrationLine(
  object: IllustrationObject | null | undefined,
): string {
  if (object === null || object === undefined)
    return 'No illustration yet. Match the scenes to pick one.';

  return `Card illustration: ${describeObject(object)}.`;
}

/**
 * One line for whichever of the four states the operator is in.
 *
 * `previous` is compared against the value the API hands back rather than against a
 * refetch, because the write and the read are one request: `rerollObject` returns the
 * column it wrote, so the two can never disagree about what happened.
 */
export function illustrationNote(state: {
  pending: boolean;
  failed: boolean;
  previous: IllustrationObject | null | undefined;
  next: IllustrationObject | undefined;
}): IllustrationNote | undefined {
  if (state.pending)
    return { severity: 'info', message: 'Reading the scenes…' };

  if (state.failed)
    return {
      severity: 'error',
      message:
        state.previous === null || state.previous === undefined
          ? 'The illustration could not be updated. This lesson still has none.'
          : `The illustration could not be updated. It is still ${name(state.previous)}.`,
    };

  if (state.next === undefined) return undefined;

  if (state.next === state.previous)
    return {
      severity: 'info',
      message: `Still ${name(state.next)}. These scenes point to the same illustration.`,
    };

  return {
    severity: 'success',
    message: `Now ${name(state.next)}. The card shows it from here.`,
  };
}
